import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Persiste o state do checkout do CRM entre as 3 páginas (Carrinho → Dados →
 * Pagamento) em sessionStorage. Idêntico ao padrão usado pelo MY checkout.
 *
 * O state é namespaced por `activeId` (cliente alvo via view-as) — ao trocar
 * de cliente o sessionStorage é descartado e começa do zero, garantindo que
 * o consultor não mistura selecções entre clientes.
 */
const KEY_PREFIX = "crm_new_order_state";

const DEFAULT = {
  selectedFatura: "",
  selectedEnvio: "",
  selectedTransport: "",
  selectedPayment: "",
  observacoes: "",
  parcelas: 1,
  mbwayPhone: "",
  // Discounts applied
  appliedPromo: "",
  appliedVales: 0,
  appliedCreditos: 0,
  // Marker indicando o step máximo atingido (cart < checkout < payment).
  maxStep: "cart",
};

export default function useNewOrderState() {
  const { activeId } = useAuth();
  const storageKey = `${KEY_PREFIX}_${activeId || "anon"}`;

  const [state, setState] = useState(() => {
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (raw) return { ...DEFAULT, ...JSON.parse(raw) };
    } catch (e) {
      // sessionStorage indisponível (private mode/quota) ou JSON corrupto.
      if (process.env.NODE_ENV !== "production") console.warn("useNewOrderState read failed:", e);
    }
    return { ...DEFAULT };
  });

  // Quando muda o `activeId`, recarrega o state correspondente do
  // sessionStorage (ou reseta se não houver). Garante que se o consultor
  // mudar de cliente posicionado a meio do checkout, o carrinho e selecções
  // do novo cliente são respeitadas.
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      setState(raw ? { ...DEFAULT, ...JSON.parse(raw) } : { ...DEFAULT });
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("useNewOrderState reload failed:", e);
      setState({ ...DEFAULT });
    }
  }, [storageKey]);

  const update = useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(next));
      } catch (e) {
        // Quota cheia ou private mode — não bloqueia o flow.
        if (process.env.NODE_ENV !== "production") console.warn("useNewOrderState save failed:", e);
      }
      return next;
    });
  }, [storageKey]);

  const reset = useCallback(() => {
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("useNewOrderState reset failed:", e);
    }
    setState({ ...DEFAULT });
  }, [storageKey]);

  return [state, update, reset];
}

export const STEP_ORDER = ["cart", "checkout", "payment"];

/** Devolve o maior dos 2 steps (mantém o progresso máximo atingido). */
export function maxStep(a, b) {
  const ia = STEP_ORDER.indexOf(a);
  const ib = STEP_ORDER.indexOf(b);
  return STEP_ORDER[Math.max(ia, ib)] || "cart";
}
